import React, {useId} from 'react';

import {MenuItem} from '@pb/kit-component-menu';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './listbox.component.module.styl';

import type {ComponentPropsWithoutRef, ReactNode} from 'react';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

type NativeProps = ComponentPropsWithoutRef<'ul'>;

export interface ListboxGroupProps extends NativeProps {
    /**
     * Label rendered above the group of items. Rendered as a non-interactive
     * `MenuItem` with `role="none"` and linked to the `<ul>` via `aria-labelledby`.
     */
    caption: ReactNode,
}

/**
 * Semantic grouping container for `ListBoxItem` children inside a `<Listbox>`.
 *
 * Renders a `<ul role="group">` with an auto-generated `aria-labelledby`
 * pointing to the `caption` label element, satisfying the WAI-ARIA Listbox
 * group pattern.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-listbox--documentation}
 */
export function ListboxGroup(props: ListboxGroupProps) {
    const {caption, children, ...nativeProps} = props;
    const id = useId();

    return (
        <ul
            role="group"
            aria-labelledby={id}
            {...nativeProps}
            className={classNames('list-box-group', nativeProps.className)}>
            <MenuItem
                separated={true}
                role="none"
                id={id}
                className={classes['list-box-group-caption']}
                caption={caption}
            />
            <div>
                {children}
            </div>
        </ul>
    );
}
